/**
 * What every running agent is holding in this room.
 *
 * Fetched when the room opens, then whatever the stream sends, which is every
 * change, a claim lapsing with nothing said included. A set the stream sent
 * beats an answer still in flight.
 */
import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { Channel, Claim } from '../api/types'
import type { OnRoomFrame } from '../live/useLiveMessages'

export function useClaims(channel: Channel | null, onRoomFrame: OnRoomFrame) {
  const [claims, setClaims] = useState<Claim[]>([])
  const channelId = channel?.id ?? null

  useEffect(() => {
    if (!channelId) {
      setClaims([])
      return
    }
    let current = true
    const stop = onRoomFrame((frame) => {
      if (frame.name !== 'claims') return
      current = false
      setClaims(frame.data)
    })
    api
      .claims(channelId)
      // A claim list that will not load leaves the button quiet, which is wrong
      // but harmless, and better than an error over a room somebody is reading.
      .then((loaded) => current && setClaims(loaded))
      .catch(() => current && setClaims([]))
    return () => {
      current = false
      stop()
    }
  }, [channelId, onRoomFrame])

  return { claims }
}
